import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  App as AntdApp,
  Button,
  DatePicker,
  Flex,
  Form,
  Input,
  Select
} from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  organizationsAccessListInfiniteOptions,
  LanguageEnum,
  organizationsAccessPartialUpdateMutation,
  usersPartialUpdateMutation,
  usersRetrieveOptions
} from 'src/api/main'
import type {
  OrganizationsAccessListData,
  PaginatedOrganizationUsersList
} from 'src/api/main'
import { InfiniteSelect, PhoneInput, ResizableDrawer } from 'src/components'
import { useHasWorkspacePermissions, useSessionContext } from 'src/hooks'
import { allLangs } from 'src/locales'
import {
  normalizePhoneForSubmit,
  WorkspacePermissionCode,
  type OptionTransformType,
  type PhoneFormValue
} from 'src/utils'

import { invalidateWorkspaceUserEditQueries } from '../hooks'
import type { WorkspaceUser } from '../types'
import { composeDisplayName } from '../utils'

type Props = {
  workspaceId: number
  open: boolean
  user: WorkspaceUser | null
  onClose: () => void
}

type FormValues = {
  first_name: string
  last_name: string
  phone?: PhoneFormValue
  position?: string | null
  job_title?: string | null
  birth_date?: Dayjs | null
  language?: LanguageEnum
  about_me?: string
  linkedin_url?: string
  head_user?: number | null
}

// `user.user.language` is typed as raw `string` in the read schema — narrow it
// to `LanguageEnum` for the AntD Select, falling back to undefined if the BE
// returns a value the frontend doesn't know about.
const asLanguageEnum = (
  value: string | null | undefined
): LanguageEnum | undefined =>
  value && (Object.values(LanguageEnum) as string[]).includes(value)
    ? (value as LanguageEnum)
    : undefined

export const UserEditDrawer = ({ workspaceId, open, user, onClose }: Props) => {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const qc = useQueryClient()
  const { message } = AntdApp.useApp()

  const identityUpdate = useMutation(usersPartialUpdateMutation())
  const managerUpdate = useMutation(organizationsAccessPartialUpdateMutation())
  const isPending = identityUpdate.isPending || managerUpdate.isPending

  // Offer only the languages this workspace enables (TS-CORE-027).
  const { user: sessionUser } = useSessionContext()
  // `job_title` lives on the workspace-access record, so editing it is a
  // membership change rather than a profile one.
  const mayManageMembers = useHasWorkspacePermissions(
    WorkspacePermissionCode.USERS_MANAGE
  )
  const languageOptions = useMemo(() => {
    const currentOrg = sessionUser?.organizations?.find(
      (organization) => Number(organization.id) === workspaceId
    )
    const enabled = currentOrg?.enabled_languages
    const langs = enabled?.length
      ? allLangs.filter((lang) => enabled.includes(lang.value))
      : allLangs

    return langs.map((lang) => ({ value: lang.value, label: lang.label }))
  }, [sessionUser, workspaceId])

  // `WorkspaceUser.user` (`UserShort`) lacks `about_me`/`linkedin_url`, so the
  // full profile is fetched to prefill those fields instead of opening blank.
  const userId = user?.user.id
  const { data: profile } = useQuery({
    ...usersRetrieveOptions({ path: { id: userId ?? 0 } }),
    enabled: open && Number.isFinite(userId) && (userId ?? 0) > 0
  })

  useEffect(() => {
    if (!open || !user) return
    form.setFieldsValue({
      first_name: user.user.first_name ?? '',
      last_name: user.user.last_name ?? '',
      phone: user.user.phone ?? '',
      position: user.user.position ?? '',
      job_title: user.job_title ?? '',
      birth_date: profile?.birth_date ? dayjs(profile.birth_date) : null,
      language: asLanguageEnum(user.user.language),
      about_me: profile?.about_me ?? '',
      linkedin_url: profile?.linkedin_url ?? '',
      head_user: user.head_user?.id ?? null
    })
  }, [open, user, profile, form])

  const submit = async (values: FormValues) => {
    if (!user) return
    try {
      await identityUpdate.mutateAsync({
        path: { id: user.user.id },
        body: {
          first_name: values.first_name,
          last_name: values.last_name,
          phone: normalizePhoneForSubmit(values.phone),
          position: values.position || null,
          language: values.language,
          about_me: values.about_me ?? '',
          linkedin_url: values.linkedin_url ?? '',
          // birth_date is a date-only field — send YYYY-MM-DD, not a datetime.
          birth_date: values.birth_date
            ? values.birth_date.format('YYYY-MM-DD')
            : null
        }
      })

      // job_title and head_user both live on the membership (access) record.
      const nextManager = values.head_user ?? null
      const prevManager = user.head_user?.id ?? null
      // Without `users.manage` the field is not rendered, so its absent value
      // must not be read as "cleared" and wipe the stored title.
      const prevJobTitle = user.job_title ?? null
      const nextJobTitle = mayManageMembers
        ? values.job_title?.trim() || null
        : prevJobTitle
      if (nextManager !== prevManager || nextJobTitle !== prevJobTitle) {
        await managerUpdate.mutateAsync({
          path: { id: user.id },
          body: { head_user: nextManager, job_title: nextJobTitle }
        })
      }

      await invalidateWorkspaceUserEditQueries(qc, {
        workspaceId,
        userId: user.user.id
      })

      message.success(t('users.editForm.saveSuccess'))
      onClose()
    } catch {
      message.error(t('users.editForm.saveFailed'))
    }
  }

  return (
    <ResizableDrawer
      open={open}
      onClose={() => {
        if (!isPending) onClose()
      }}
      defaultWidth={520}
      storageKey="users-edit-drawer-width"
      title={t('users.editForm.title')}
      footer={
        <Flex justify="flex-end" gap={8}>
          <Button onClick={onClose} disabled={isPending}>
            {t('users.editForm.cancel')}
          </Button>
          <Button
            type="primary"
            loading={isPending}
            onClick={() => form.submit()}
          >
            {t('users.editForm.save')}
          </Button>
        </Flex>
      }
    >
      <Form<FormValues> form={form} layout="vertical" onFinish={submit}>
        <Form.Item
          name="first_name"
          label={t('users.detail.identity.firstName')}
          rules={[
            { required: true, message: t('users.editForm.required.firstName') }
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="last_name"
          label={t('users.detail.identity.lastName')}
          rules={[
            { required: true, message: t('users.editForm.required.lastName') }
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="phone" label={t('users.detail.identity.phone')}>
          <PhoneInput
            key={
              open
                ? `phone-${user?.user.id ?? ''}-${user?.user.phone ?? ''}`
                : 'phone-closed'
            }
          />
        </Form.Item>
        <Form.Item
          name="birth_date"
          label={t('users.detail.identity.birthDate')}
        >
          <DatePicker
            placeholder={t('users.editForm.birthDatePlaceholder')}
            style={{ width: '100%' }}
            disabledDate={(current) => current && current.isAfter(dayjs())}
          />
        </Form.Item>
        <Form.Item name="position" label={t('users.detail.identity.position')}>
          <Input />
        </Form.Item>
        {mayManageMembers ? (
          <Form.Item
            name="job_title"
            label={t('users.detail.identity.jobTitle')}
          >
            <Input />
          </Form.Item>
        ) : null}
        <Form.Item
          name="head_user"
          label={t('users.editForm.manager')}
          extra={t('users.editForm.managerHint')}
        >
          <InfiniteSelect<
            OrganizationsAccessListData,
            PaginatedOrganizationUsersList,
            WorkspaceUser
          >
            allowClear
            getQueryFunction={organizationsAccessListInfiniteOptions}
            baseOptions={{
              query: {
                organization: workspaceId,
                // Pin the current manager first so its option loads and the
                // label renders (`preference` keys off the access-record id).
                preference: user?.head_user?.organization_access_id
                  ? String(user.head_user.organization_access_id)
                  : undefined
              }
            }}
            getOptions={({ options }: OptionTransformType<WorkspaceUser>) =>
              // A user can't be their own manager.
              options
                .filter((u) => u.user.id !== user?.user.id)
                .map((u) => ({
                  value: u.user.id,
                  label: `${composeDisplayName(
                    u.user.first_name,
                    u.user.last_name,
                    u.user.email
                  )} (${u.user.email})`
                }))
            }
            placeholder={t('users.editForm.managerPlaceholder')}
          />
        </Form.Item>
        <Form.Item name="language" label={t('users.detail.identity.language')}>
          <Select allowClear options={languageOptions} />
        </Form.Item>
        <Form.Item name="linkedin_url" label="LinkedIn">
          <Input placeholder="https://linkedin.com/in/…" />
        </Form.Item>
        <Form.Item name="about_me" label={t('users.editForm.aboutMe')}>
          <Input.TextArea rows={3} maxLength={1000} showCount />
        </Form.Item>
      </Form>
    </ResizableDrawer>
  )
}
